const HOOP_X = 0;
const HOOP_Z = 5.25;
const COURT_HALF_WIDTH = 25;
const COURT_HALF_LENGTH = 47;

const SHOT_CLASS_ALIASES = {
  'at rim': 'At Rim',
  rim: 'At Rim',
  layup: 'At Rim',
  dunk: 'At Rim',
  paint: 'Paint',
  lane: 'Paint',
  floater: 'Paint',
  'mid-range': 'Mid-Range',
  midrange: 'Mid-Range',
  mid: 'Mid-Range',
  elbow: 'Mid-Range',
  'three point': '3PT',
  'three-point': '3PT',
  three: '3PT',
  perimeter: '3PT',
  '3pt': '3PT',
  '3': '3PT',
};

const ZONE_ANCHORS = {
  'At Rim': { x: 0, z: 7 },
  Paint: { x: 5, z: 11 },
  'Mid-Range': { x: -10, z: 19 },
  '3PT': { x: 19, z: 15 },
};

const BI_V2_SHOT_SCHEMA = {
  schemaVersion: 'biv2.0',
  shotFields: [
    'id',
    'x',
    'z',
    'made',
    'type',
    'shotClass',
    'quarter',
    'time',
    'dist',
    'defDist',
    'ballFromHoop',
    'contestLevel',
    'description',
    'videoUrl',
    'expectedFgPct',
    'movementOrigin',
    'lastFiveSteps',
    'footworkStyle',
    'passOrigin',
  ],
  zoneOrder: ['At Rim', 'Paint', 'Mid-Range', '3PT'],
  contestLevels: ['Wide Open', 'Open', 'Contested', 'Tight'],
};

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function read(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of keys) {
    const parts = key.split('.');
    let current = obj;
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        current = undefined;
        break;
      }
    }
    if (current !== undefined && current !== null) return current;
  }
  return undefined;
}

function clampShotCoordinate(x, z) {
  const cx = Math.max(-COURT_HALF_WIDTH + 0.5, Math.min(COURT_HALF_WIDTH - 0.5, x));
  const cz = Math.max(0.5, Math.min(COURT_HALF_LENGTH - 0.5, z));
  return { x: cx, z: cz };
}

function normalizeShotClass(raw, distanceFt) {
  const candidates = [
    read(raw, ['shotClass', 'shot_class', 'zone', 'zone_name', 'region', 'location']),
    read(raw, ['type', 'shot_type', 'action']),
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase().trim());

  for (const candidate of candidates) {
    for (const [alias, normalized] of Object.entries(SHOT_CLASS_ALIASES)) {
      if (candidate.includes(alias)) return normalized;
    }
  }

  if (distanceFt !== null) {
    if (distanceFt <= 4) return 'At Rim';
    if (distanceFt <= 10) return 'Paint';
    if (distanceFt >= 22) return '3PT';
  }

  return 'Mid-Range';
}

function normalizeMade(raw) {
  const direct = read(raw, ['made', 'is_made', 'shot_made']);
  if (typeof direct === 'boolean') return direct;
  if (typeof direct === 'number') return direct > 0;

  const resultText = String(read(raw, ['result', 'shot_result', 'outcome']) || '').toLowerCase();
  if (!resultText) return false;
  return ['made', 'make', 'hit', 'good'].some((t) => resultText.includes(t));
}

function normalizeContestLevel(defDist, rawContest) {
  const normalizedRaw = String(rawContest || '').trim();
  if (normalizedRaw) {
    const lowered = normalizedRaw.toLowerCase();
    if (lowered.includes('wide')) return 'Wide Open';
    if (lowered.includes('open')) return 'Open';
    if (lowered.includes('tight')) return 'Tight';
    if (lowered.includes('contest')) return 'Contested';
  }

  if (defDist === null) return 'Open';
  if (defDist >= 6) return 'Wide Open';
  if (defDist >= 4) return 'Open';
  if (defDist >= 2) return 'Contested';
  return 'Tight';
}

function inferCoordinates(raw, shotClass, index) {
  const x = toNumber(read(raw, ['x', 'shot_x', 'court_x', 'loc_x', 'coordinates.x']));
  const z = toNumber(read(raw, ['z', 'y', 'shot_y', 'court_y', 'loc_y', 'coordinates.y']));

  if (x !== null && z !== null) return clampShotCoordinate(x, z);

  const anchor = ZONE_ANCHORS[shotClass] || ZONE_ANCHORS['Mid-Range'];
  const offsetX = ((index % 5) - 2) * 1.3;
  const offsetZ = ((Math.floor(index / 5) % 4) - 1.5) * 1.1;
  return clampShotCoordinate(anchor.x + offsetX, anchor.z + offsetZ);
}

function normalizeExpectedFgPct(raw) {
  const value = toNumber(read(raw, ['expectedFgPct', 'xfg', 'xfg_pct', 'expected_fg_pct', 'expected_field_goal_pct']));
  if (value === null) return null;
  if (value > 1) return Math.max(0, Math.min(1, value / 100));
  return Math.max(0, Math.min(1, value));
}

function normalizeStepList(rawValue) {
  if (Array.isArray(rawValue)) {
    return rawValue
      .map((step) => String(step).trim())
      .filter(Boolean)
      .slice(0, 5);
  }

  if (typeof rawValue === 'string') {
    return rawValue
      .split(/\s*(?:>|->|\||,|;)\s*/)
      .map((step) => step.trim())
      .filter(Boolean)
      .slice(0, 5);
  }

  return [];
}

function normalizeOneShot(rawShot, index) {
  const dist = toNumber(read(rawShot, ['dist', 'distance', 'distance_ft', 'shot_distance', 'shot_distance_ft']));
  const shotClass = normalizeShotClass(rawShot, dist);
  const point = inferCoordinates(rawShot, shotClass, index);

  const ballFromHoop =
    toNumber(read(rawShot, ['ballFromHoop', 'ball_from_hoop', 'release_to_rim_ft'])) ||
    Math.sqrt((point.x - HOOP_X) ** 2 + (point.z - HOOP_Z) ** 2);

  const defDist = toNumber(read(rawShot, ['defDist', 'defender_distance', 'defender_distance_ft', 'closest_defender_ft']));
  const contestLevel = normalizeContestLevel(defDist, read(rawShot, ['contestLevel', 'contest_level', 'contest']));
  const made = normalizeMade(rawShot);
  const expectedFgPct = normalizeExpectedFgPct(rawShot);
  const lastFiveSteps = normalizeStepList(read(rawShot, [
    'lastFiveSteps',
    'last_five_steps',
    'movement.last_five_steps',
    'movement.steps',
    'module_2_footwork.last_five_steps',
    'module_2_footwork.step_sequence',
    'module_2_footwork.approach_steps',
  ]));

  const type = String(
    read(rawShot, ['type', 'shot_type', 'action']) ||
    (shotClass === '3PT' ? 'Three-Point' : shotClass)
  );
  const movementOrigin = String(read(rawShot, [
    'movementOrigin',
    'movement_origin',
    'movement.start_zone',
    'entry_origin',
    'module_2_footwork.movement_origin',
    'module_2_footwork.entry_origin',
    'module_2_footwork.origin_zone',
  ]) || 'Unknown origin');
  const footworkStyle = String(read(rawShot, [
    'footworkStyle',
    'footwork_style',
    'movement.footwork_style',
    'module_2_footwork.footwork_style',
    'module_2_footwork.footwork_type',
    'module_2_footwork.catch_footwork',
  ]) || 'unknown');
  const passOrigin = String(read(rawShot, [
    'passOrigin',
    'pass_origin',
    'pass_source',
    'pass.origin',
    'module_4_cognitive.pass_origin',
    'module_4_cognitive.pass_source',
  ]) || 'Unknown');

  return {
    id: read(rawShot, ['id', 'shot_id']) || `shot_${index + 1}`,
    x: point.x,
    z: point.z,
    made,
    type,
    shotClass,
    quarter: String(read(rawShot, ['quarter', 'period']) || 'Q1'),
    time: String(read(rawShot, ['time', 'clock', 'game_clock']) || '--:--'),
    dist: dist === null ? Math.round(ballFromHoop * 10) / 10 : dist,
    defDist: defDist === null ? 4.5 : defDist,
    ballFromHoop: Math.round(ballFromHoop * 10) / 10,
    contestLevel,
    description: String(
      read(rawShot, ['description', 'form_notes', 'notes']) ||
      `${type} from ${shotClass}`
    ),
    videoUrl: String(read(rawShot, ['videoUrl', 'video_url', 'clip_url', 'url']) || '#'),
    expectedFgPct,
    movementOrigin,
    lastFiveSteps,
    footworkStyle,
    passOrigin,
  };
}

function flattenCandidateShots(input) {
  if (!input) return [];

  if (Array.isArray(input)) {
    const looksLikeShots = input.some(
      (entry) => entry && typeof entry === 'object' && (
        'x' in entry ||
        'z' in entry ||
        'made' in entry ||
        'shot_result' in entry ||
        'shot_type' in entry
      )
    );
    if (looksLikeShots) return input;

    return input.flatMap((entry) => {
      if (!entry || typeof entry !== 'object') return [];
      return flattenCandidateShots(entry);
    });
  }

  if (typeof input === 'object') {
    const direct = read(input, ['shots', 'shotMatrix.shots', 'analysis.shooting.shots', 'shooting.shots']);
    if (Array.isArray(direct)) return direct;

    const segments = read(input, ['segments', 'segmentAnalyses', 'analysis.segments']);
    if (Array.isArray(segments)) {
      return segments.flatMap((segment) => {
        const shots = read(segment, ['shooting.shots', 'analysis.shooting.shots', 'shots']);
        return Array.isArray(shots) ? shots : [];
      });
    }
  }

  return [];
}

function pickPlayerMeta(input, fallbackPlayer = {}) {
  if (!input || typeof input !== 'object') return fallbackPlayer;

  const playerContext = read(input, ['player', 'playerContext', 'meta.player']) || {};
  return {
    name: playerContext.name || playerContext.playerName || fallbackPlayer.name || 'PLAYER',
    number: playerContext.number || playerContext.jersey || fallbackPlayer.number || '--',
    team: playerContext.team || playerContext.school || fallbackPlayer.team || 'TEAM',
    game: read(input, ['meta.game', 'game', 'gameLabel']) || fallbackPlayer.game || 'Video Analysis',
  };
}

function buildSummary(shots) {
  const attempts = shots.length;
  const makes = shots.filter((s) => s.made).length;
  const points = shots.reduce((sum, s) => {
    if (!s.made) return sum;
    return sum + (s.shotClass === '3PT' ? 3 : 2);
  }, 0);
  const pps = attempts > 0 ? points / attempts : 0;
  const threeMakes = shots.filter((s) => s.made && s.shotClass === '3PT').length;
  const efg = attempts > 0 ? (makes + 0.5 * threeMakes) / attempts : 0;

  return {
    attempts,
    makes,
    misses: attempts - makes,
    points,
    pps,
    efg,
  };
}

export function normalizeBiV2ShotMatrix(input, options = {}) {
  const { fallbackShots = [], fallbackPlayer = null } = options;
  const candidates = flattenCandidateShots(input);
  const rawShots = candidates.length > 0 ? candidates : fallbackShots;
  const shots = rawShots.map((shot, index) => normalizeOneShot(shot, index));

  return {
    schema: BI_V2_SHOT_SCHEMA,
    shots,
    player: pickPlayerMeta(input, fallbackPlayer || {}),
    summary: buildSummary(shots),
  };
}

export { BI_V2_SHOT_SCHEMA };
